import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buildJurisdictionMaps,
  CONTRACT_KIND_LABELS,
  DOCUMENT_SCOPES,
  docusealService,
  getDocusealErrorMessage,
  type ContractDocument,
  type DocumentScope,
} from "@/services/docuseal";
import { queryKeys } from "@/queryKeys";
import DeleteTemplateButton from "@/components/documents/DeleteTemplateButton";
import TemplatePreviewButton from "@/components/documents/TemplatePreviewButton";
import TrialLocked from "@/components/trial/TrialLocked";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  deleteUnavailableReason,
  documentTypeLabel,
  invalidateDocumentQueries,
  MessageRow,
  missingRequiredKinds,
  SkeletonRows,
} from "@/pages/admin/documentPagesShared";

const COLUMNS = 3;

/** A template's type and its enabled and default state. */
const TemplateType: React.FC<{
  doc: ContractDocument;
  jurisdictionNames: Record<string, string>;
  children?: React.ReactNode;
}> = ({ doc, jurisdictionNames, children }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
    <span className="text-sm">{documentTypeLabel(doc, jurisdictionNames)}</span>
    {!doc.is_active && <Badge variant="outline">not enabled</Badge>}
    {doc.is_default && <Badge variant="secondary">default</Badge>}
    {children}
  </div>
);

const Documents: React.FC<{ scope?: DocumentScope }> = ({ scope = DOCUMENT_SCOPES.org }) => {
  const { service } = scope;
  const queryClient = useQueryClient();
  const documentsQuery = useQuery({
    queryKey: queryKeys.contractAdminDocuments(scope.key),
    queryFn: () => service.listAdminDocuments(),
  });
  const missingKinds =
    scope.requiresDefaults && documentsQuery.isSuccess
      ? missingRequiredKinds(documentsQuery.data)
      : [];
  const jurisdictionsQuery = useQuery({
    queryKey: queryKeys.jurisdictions(),
    queryFn: () => docusealService.listJurisdictions(),
  });
  const jurisdictionNames = useMemo(
    () => buildJurisdictionMaps(jurisdictionsQuery.data?.jurisdictions ?? []).byCode,
    [jurisdictionsQuery.data]
  );

  const [actionError, setActionError] = useState<string | null>(null);

  // The scope's own templates, and the shared system templates listed read-only beside them.
  const [rows, sharedDocs] = useMemo(() => {
    const docs = documentsQuery.data ?? [];
    return [
      docs.filter((doc) => doc.is_system === scope.isSystem),
      docs.filter((doc) => doc.is_system !== scope.isSystem),
    ];
  }, [documentsQuery.data, scope.isSystem]);
  const loadError = documentsQuery.isError ? getDocusealErrorMessage(documentsQuery.error) : null;

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateId: number) => service.deleteRemoteTemplate(templateId),
    onSuccess: () => invalidateDocumentQueries(queryClient),
    onError: (err) => setActionError(getDocusealErrorMessage(err)),
  });

  const disableMutation = useMutation({
    mutationFn: (docId: number) => service.updateDocument(docId, { is_active: false }),
    onSuccess: () => invalidateDocumentQueries(queryClient),
    onError: (err) => setActionError(getDocusealErrorMessage(err)),
  });

  const makeDefaultMutation = useMutation({
    mutationFn: (docId: number) => service.updateDocument(docId, { is_default: true }),
    onSuccess: () => invalidateDocumentQueries(queryClient),
    onError: (err) => setActionError(getDocusealErrorMessage(err)),
  });

  return (
    <div className="space-y-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-3xl font-semibold tracking-tight">{scope.title}</h1>
        <TrialLocked lock="contractDocuments" inline className="self-start sm:self-auto">
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" asChild>
              <Link to={`${scope.basePath}/new`}>
                <Plus className="h-4 w-4" />
                New template
              </Link>
            </Button>
          </div>
        </TrialLocked>
      </div>

      {missingKinds.length > 0 && (
        <Alert>
          <AlertDescription>
            Missing a default for{" "}
            {missingKinds.map((kind) => CONTRACT_KIND_LABELS[kind]).join(", ")}. Sending the
            merchant flow will fail until one is enabled and set as default.
          </AlertDescription>
        </Alert>
      )}

      {(loadError || actionError) && (
        <Alert variant="destructive">
          <AlertDescription>
            {loadError && <div>{loadError}</div>}
            {actionError && <div>{actionError}</div>}
          </AlertDescription>
        </Alert>
      )}

      {sharedDocs.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>System template</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[1%] text-right">
                    <span className="sr-only">View</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sharedDocs.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell className="font-medium">{doc.name}</TableCell>
                    <TableCell>
                      <TemplateType doc={doc} jurisdictionNames={jurisdictionNames}>
                        <Badge variant="outline">System</Badge>
                      </TemplateType>
                    </TableCell>
                    <TableCell className="text-right">
                      <TemplatePreviewButton documentId={doc.id} onError={setActionError} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-[1%] text-right">
                  <span className="sr-only">Delete</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documentsQuery.isLoading ? (
                <SkeletonRows columns={COLUMNS} />
              ) : documentsQuery.isError ? (
                <MessageRow columns={COLUMNS} muted={false}>
                  Could not load templates.
                </MessageRow>
              ) : rows.length === 0 ? (
                <MessageRow columns={COLUMNS}>
                  No templates yet. Create one with New template.
                </MessageRow>
              ) : (
                rows.map((doc) => {
                  const makingDefault =
                    makeDefaultMutation.isPending && makeDefaultMutation.variables === doc.id;
                  return (
                    <TableRow key={doc.id} className="group">
                      <TableCell className="font-medium">
                        <Link
                          to={`${scope.basePath}/${doc.docuseal_template_id}/edit`}
                          className="text-primary hover:underline"
                        >
                          {doc.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <TemplateType doc={doc} jurisdictionNames={jurisdictionNames}>
                          {doc.is_active && !doc.is_default && (
                            <TrialLocked lock="contractDocuments" inline>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="pointer-events-none h-6 px-2 text-xs opacity-0 transition-opacity focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
                                disabled={makingDefault}
                                onClick={() => {
                                  setActionError(null);
                                  makeDefaultMutation.mutate(doc.id);
                                }}
                              >
                                {makingDefault && <Loader2 className="h-3 w-3 animate-spin" />}
                                make default
                              </Button>
                            </TrialLocked>
                          )}
                        </TemplateType>
                      </TableCell>
                      <TableCell className="text-right">
                        <TrialLocked lock="contractDocuments" inline>
                          <div className="flex items-center justify-end gap-2">
                            {doc.is_active && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={
                                  disableMutation.isPending && disableMutation.variables === doc.id
                                }
                                onClick={() => {
                                  setActionError(null);
                                  disableMutation.mutate(doc.id);
                                }}
                              >
                                Disable
                              </Button>
                            )}
                            <DeleteTemplateButton
                              templateName={doc.name}
                              unavailableReason={deleteUnavailableReason(doc.is_active)}
                              busy={
                                deleteTemplateMutation.isPending &&
                                deleteTemplateMutation.variables === doc.docuseal_template_id
                              }
                              onConfirm={() => {
                                setActionError(null);
                                deleteTemplateMutation.mutate(doc.docuseal_template_id);
                              }}
                            />
                          </div>
                        </TrialLocked>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Documents;
